/**
 * 视频生成模型选择 —— P2-3 第二刀(2026-05-30)从 VideoCreatePage 高级面板抽出。
 * 智能选择(按素材自动挑选)/ 手动指定(provider 分组网格)。约束派生仍在
 * useVideoProviderConstraints,本组件只渲染 + 写回 form。
 *
 * ═══ 2026-09-13 D 批:收成一行紧凑控件(toB 不要大选择框)═══════════════════════════
 * 老板拍板:模型块**默认收起成一行**「模型  智能选 · 豆包 Seedance ▾」,点开才展开
 * 智能/手动 + 厂商网格。多数 toB 用户不换模型 —— 一进来就铺一张大 RadioGroup + 网格
 * 是主次不分。展开态逻辑一字未改,只是外面套了个默认收起的壳;手动选型进行中(已切到手动)
 * 时默认展开,免得用户看不到自己选的是哪张。
 *
 * ═══ OEM H2(2026-10-01):卡面回用后端 label / tagline ═══════════════════════════════
 * 后端能力表直出厂商中立的档位名(「旗舰·综合」「标准·图生视频」,单一真源 api VideoModelTiers),
 * 前端不再自己映射 / 脱敏;网格按质量档(tier / tierRank)分组,不再按厂商 provider 分组。
 * 超管非代客态卡面附真名(videoProviderLabel 的 reveal 口径)。
 */
import { useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { ChevronDown } from "lucide-react";

import { Label } from "@/shared/ui/label";
import { RadioGroup, RadioGroupItem } from "@/shared/ui/radio-group";
import type { FormState } from "../../model/videoCreateTypes";
import type { VideoCapabilitiesResponse, VideoProviderCapability } from "../../api/videoService";
import { generationModeLabel, videoProviderLabel } from "@/shared/lib/video/labels";
import { maskVendor } from "@/shared/lib/vendorMask";
import { usePriceBook } from "@/entities/pricing/model/usePriceBook";
import { perSecondText, stripCheapClaim, tierPerSecond } from "../../model/videoTierPricing";

/** 质量档分组:按 tierRank 升序(老后端无 tier → 归「其他」排最后)。 */
function groupByTier(
  providers: readonly VideoProviderCapability[]
): Array<[string, VideoProviderCapability[]]> {
  const groups = new Map<string, { rank: number; items: VideoProviderCapability[] }>();
  for (const p of providers) {
    const key = p.tier?.trim() || "其他";
    const rank = p.tierRank ?? Number.MAX_SAFE_INTEGER;
    const g = groups.get(key);
    if (g) {
      g.items.push(p);
      g.rank = Math.min(g.rank, rank);
    } else groups.set(key, { rank, items: [p] });
  }
  return [...groups.entries()].sort((a, b) => a[1].rank - b[1].rank).map(([k, g]) => [k, g.items]);
}

interface Props {
  caps: VideoCapabilitiesResponse;
  form: FormState;
  setForm: Dispatch<SetStateAction<FormState>>;
  effectiveModel: { provider: string; model: string };
  effectiveMode: string;
  setModelProvider: (provider: string, model: string) => void;
}

export function VideoModelSelect({
  caps,
  form,
  setForm,
  effectiveModel,
  effectiveMode,
  setModelProvider,
}: Props) {
  // 收起态:一行摘要「模型  智能选 · X ▾」。手动选型时默认展开(否则看不到选了哪张)。
  const [expanded, setExpanded] = useState(!form.useAutoModel);
  // 1004 T5:每档卡面标每秒价 —— 价目表(与报价口同值)现取,当前分辨率下的规格档单价。
  const { book } = usePriceBook();

  return (
    <div className="space-y-2">
      {/* ── 紧凑摘要行(常显)── */}
      <div className="flex items-center gap-2">
        <Label className="shrink-0 text-sm">模型</Label>
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
          {form.useAutoModel ? "智能选 · " : "手动 · "}
          <span className="font-medium text-foreground">
            {videoProviderLabel(effectiveModel.provider, effectiveModel.model)}
          </span>
        </span>
        <button
          type="button"
          data-testid="video-model-expand"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          className="inline-flex shrink-0 cursor-pointer items-center gap-0.5 rounded text-2xs text-primary transition-colors hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {expanded ? "收起" : "手动指定"}
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {/* ── 展开态:智能 / 手动 + 厂商网格(点开才出,逻辑原样)── */}
      {expanded && (
        <div className="space-y-3 border-t border-border/60 pt-3">
          <RadioGroup
            value={form.useAutoModel ? "auto" : "manual"}
            onValueChange={(v) => setForm((s) => ({ ...s, useAutoModel: v === "auto" }))}
            className="flex gap-4"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="auto" id="auto-model" />
              <Label htmlFor="auto-model" className="text-sm font-normal cursor-pointer">
                智能选择(推荐 — 按素材自动挑选)
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="manual" id="manual-model" />
              <Label htmlFor="manual-model" className="text-sm font-normal cursor-pointer">
                手动指定
              </Label>
            </div>
          </RadioGroup>
          {form.useAutoModel && (
            <p className="text-xs text-muted-foreground">
              当前自动选用:
              <span className="text-foreground font-medium ml-1">
                {videoProviderLabel(effectiveModel.provider, effectiveModel.model)}
              </span>
              {" · "}
              {generationModeLabel(effectiveMode)}
            </p>
          )}

          {/* 手动模式 → 显示 Provider 网格 */}
          {!form.useAutoModel && (
            <div className="space-y-3">
              {groupByTier(caps.providers).map(([tierKey, models]) => (
                <div key={tierKey} className="rounded-md border bg-card/30 p-2">
                  <div className="text-xs font-medium mb-2 px-1">{tierKey}</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {models.map((p) => {
                      const active = form.provider === p.provider && form.model === p.model;
                      const price = tierPerSecond(book, p, form.resolution);
                      return (
                        <button
                          key={`${p.provider}-${p.model}`}
                          type="button"
                          className={`text-left rounded-md border p-2 transition cursor-pointer ${
                            active
                              ? "ring-2 ring-primary border-primary bg-primary/5"
                              : "hover:border-primary/50 hover:bg-muted/30"
                          }`}
                          onClick={() => setModelProvider(p.provider, p.model)}
                        >
                          {/* ★OEM H2:后端 label / tagline 已是厂商中立的档位名与能力描述,直接用。
                              带 tier 才算新后端;滚动发版窗口里老后端(label 是真名、无 tier)
                              回落镜像表 + 脱敏,api H2 全量上线后这层兼容可删。 */}
                          <div className="font-medium text-xs leading-tight mb-1">
                            {videoProviderLabel(p.provider, p.model, p.tier ? p.label : null)}
                          </div>
                          <div className="text-2xs text-muted-foreground line-clamp-2">
                            {stripCheapClaim(p.tier ? p.tagline : maskVendor(p.tagline))}
                          </div>
                          {price && (
                            <div
                              className="mt-1 text-2xs font-medium text-amber-600 tabular-nums"
                              data-testid={`video-tier-price-${p.model}`}
                            >
                              {perSecondText(price.rate)}
                              {price.resolution !== form.resolution ? `(${price.resolution})` : ""}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
